import { useEffect, useRef, useState } from "react";

import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { Tooltip } from "@/components/tooltip/Tooltip";
import { twMerge } from "@/utils/twMerge";

import type { PolymorphicProps } from "../expression/utils/NodeLayout";
import type { ComponentProps, ComponentPropsWithRef, ReactNode } from "react";

type SlotProps = {
  children?: ReactNode;
  className?: string;
};

type OptionElementProps<T extends "div" | "button"> = Omit<PolymorphicProps<T>, "title"> & {
  ref?: ComponentPropsWithRef<T>["ref"];
};

export type OptionRootProps<T extends "div" | "button"> = {
  label?: string;
  keyShortcuts?: string;
  isActive?: boolean;
  isHighlighted?: boolean;
  isFocused?: boolean;
  disableHoverStyles?: boolean;
  checked?: boolean;
  disabled?: boolean;
} & OptionElementProps<T>;

export function OptionRoot<T extends "div" | "button" = "button">({
  label,
  keyShortcuts,
  isActive,
  isHighlighted,
  isFocused,
  checked,
  className,
  disableHoverStyles = false,
  disabled = false,
  as: Component = "button" as T,
  ...rest
}: OptionRootProps<T>) {
  return (
    <Component
      tabIndex={isActive ? 0 : -1}
      aria-label={label}
      aria-disabled={disabled || undefined}
      aria-keyshortcuts={keyShortcuts}
      className={twMerge(
        "font-sans text-sm tracking-tight flex w-full flex-nowrap items-center rounded-8 min-h-8.5 px-2 data-[focus]:bg-blue-100 data-[focus]:dark:bg-blue-900",
        "sm:text-[16px]",
        !disableHoverStyles && "hover:bg-blue-100 dark:hover:bg-gray-900",
        isFocused && "ring-2 ring-inset ring-blue-100 dark:ring-gray-900",
        isHighlighted && "bg-gray-100 dark:bg-gray-950",
        isActive && "bg-blue-100 dark:bg-gray-900",
        className,
        disabled && "opacity-50 pointer-events-none cursor-default",
      )}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      {...(rest as ComponentProps<any>)}
      {...(checked !== undefined ? { role: "menuitemcheckbox", "aria-checked": checked } : {})}
    />
  );
}

export function OptionIcon({ children, className }: SlotProps) {
  return (
    <div className={twMerge("mr-1.5 min-w-6 flex items-center justify-center", className)}>
      {children}
    </div>
  );
}

export function OptionContent({
  children,
  className,
  truncate = true,
}: SlotProps & { truncate?: boolean }) {
  return (
    <div
      className={twMerge(
        "flex-1 text-left py-px",
        truncate ? "truncate" : "whitespace-nowrap",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function OptionTitle({
  children,
  className,
  truncate = true,
  withSeparator = false,
}: SlotProps & { truncate?: boolean; withSeparator?: boolean }) {
  const [isTruncated, setIsTruncated] = useState(false);
  const titleRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (titleRef.current && truncate) {
      const truncated = titleRef.current.scrollWidth > titleRef.current.clientWidth;
      setIsTruncated(truncated);
    }
  }, [truncate, children]);

  return (
    <span
      ref={titleRef}
      className={twMerge(
        "mr-[3px]",
        withSeparator && "before:text-gray-500 before:content-['·'] before:mr-[6px]",
        className,
      )}
    >
      <Tooltip disabled={!isTruncated} title={children}>
        {children}&nbsp;
      </Tooltip>
    </span>
  );
}

export function OptionSubTitle({ children, className }: SlotProps) {
  return (
    <span
      className={twMerge(
        "text-gray-500 pb-[1px] text-nowrap",
        "before:text-gray-500 before:content-['·'] before:mr-[6px]",
        className,
      )}
    >
      {children}
    </span>
  );
}

export function OptionAction({ children, className }: SlotProps) {
  return (
    <div
      aria-hidden
      className={twMerge(
        "ml-auto pl-2.5 text-[13px] min-w-10 text-xxs text-gray-400",
        "sm:hidden",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function OptionDoneLabel({ className }: Omit<SlotProps, "children">) {
  const t = useTranslations();

  return (
    <div
      aria-hidden
      className={twMerge(
        "hidden ml-3 pl-1.5 text-[13px] min-w-10 text-xxs text-gray-400",
        "sm:flex",
        className,
      )}
    >
      {t("suggestions.done")}
    </div>
  );
}

export function OptionCheck({ className }: Omit<SlotProps, "children">) {
  return <Icon name="Check" className={twMerge("w-6 h-6 ml-3", "sm:hidden", className)} />;
}

export function OptionTrailing({
  action,
  isActive,
  checked,
  actionClassName,
}: {
  action?: ReactNode;
  isActive?: boolean;
  checked?: boolean;
  actionClassName?: string;
}) {
  if (checked) {
    return <OptionCheck />;
  }

  return (
    <>
      <OptionAction className={actionClassName}>{action}</OptionAction>
      {isActive && <OptionDoneLabel />}
    </>
  );
}

type Props<T extends "div" | "button"> = {
  title?: ReactNode;
  subTitle?: ReactNode;
  label?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  keyShortcuts?: string;
  isActive?: boolean;
  isHighlighted?: boolean;
  isFocused?: boolean;
  disableHoverStyles?: boolean;
  checked?: boolean;
  truncate?: boolean;
  disabled?: boolean;
  withSeparator?: boolean;
} & OptionElementProps<T>;

export function Option<T extends "div" | "button" = "button">({
  title,
  subTitle,
  label,
  icon,
  action,
  keyShortcuts,
  isActive,
  isHighlighted,
  isFocused,
  checked,
  truncate = true,
  className,
  disableHoverStyles = false,
  disabled = false,
  withSeparator = !!icon,
  as: Component = "button" as T,
  ...rest
}: Props<T>) {
  return (
    <OptionRoot
      as={Component}
      label={label}
      keyShortcuts={keyShortcuts}
      isActive={isActive}
      isHighlighted={isHighlighted}
      isFocused={isFocused}
      checked={checked}
      className={className}
      disableHoverStyles={disableHoverStyles}
      disabled={disabled}
      {...(rest as OptionElementProps<T>)}
    >
      {icon && <OptionIcon className={twMerge(!withSeparator && "mr-2.5")}>{icon}</OptionIcon>}
      <OptionContent truncate={truncate}>
        {title && (
          <OptionTitle truncate={truncate} withSeparator={withSeparator}>
            {title}
          </OptionTitle>
        )}
        {subTitle && <OptionSubTitle>{subTitle}</OptionSubTitle>}
      </OptionContent>
      <OptionTrailing action={action} isActive={isActive} checked={checked} />
    </OptionRoot>
  );
}
